"use client"

import type React from "react"
import { useEffect, useRef, useState, useCallback } from "react"
import type { Enemy } from "@/types/enemy.types"
import type { Tower, TowerType } from "@/types/tower.types"
import type { Hero, ActiveSkillEffect } from "@/types/hero.types"
import type { Projectile } from "@/types/projectile.types"
import type { LevelConfig, Position, ParticleEffect, ViewportState, LandedPod, EntanglingRootsEffect, Corpse, AoeVisualEffect, LaserBeamEffect, AbductionAnimationState, SoldierSpawnPortrait, ItemDropMessage } from "@/types/game.types"
import type { SummonedUnit, SummonType } from "@/types/summon.types"
import type { BarracksSoldier } from "@/types/barracks.types"
import type { SkillType } from "@/types/hero.types"
import type { ItemEffectType, ActiveItemEffect, DroppedItem } from "@/types/item.types"
import type { UpgradeModifiers, UpgradeState } from "@/types/upgrade.types"
import {
  drawEnemy,
  drawTower,
  drawHero,
  drawProjectile,
  drawPath,
  drawBase,
  drawSkillEffect,
  drawSummonedWolf,
  drawSummonedFox,
  drawSummonedBoar,
  drawSummonedTower,
  drawSlowEffect,
  drawEntanglingRoots,
  drawRootEffect,
  drawCorpse,
  drawLevelTilemap,
  drawFireGroundEffect,
  drawLaserBeam,
  drawTurretPreview,
  drawAbductionBeam,
  drawBarracksSoldier,
  drawRallyPoint,
  drawSoldierRespawnBar,
  drawHealthBar,
  drawSoldierSpawnPortrait,
  setOverlayScale,
} from "@/utils/rendering"
import { drawTMXMap, drawTMXElevationRow } from "@/utils/tmx-renderer"
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT, TILE_SIZE, WORLD_WIDTH } from "@/data/game-settings"
import { gameSettings } from "@/data/game-settings"
import { worldToTile } from "@/utils/tile-utils"
import { towerTypes } from "@/data/tower-types"
import { getSoldierRespawnProgress, getRallyRadius } from "@/systems/barracks-system"
import { getShrineEffectRadius } from "@/systems/shrine-system"
import { TooltipProvider } from "@/components/ui/tooltip"
import { enemyTypes } from "@/data/enemy-types"
import { getItemDefinition } from "@/systems/item-system"
import { useViewport } from "@/contexts/viewport-context"
import { gameNow } from "@/utils/game-clock"

interface GameCanvasProps {
  enemies: Enemy[]
  towers: Tower[]
  hero: Hero | null
  projectiles: Projectile[]
  skillEffects?: ActiveSkillEffect[]
  summonedUnits?: SummonedUnit[]
  rootsEffects?: EntanglingRootsEffect[]
  fireEffects?: import("@/types/game.types").FireGroundEffect[]
  laserBeamEffects?: LaserBeamEffect[]
  barracksSoldiers?: BarracksSoldier[]
  selectedSoldierId?: string | null
  selectedEnemyId?: string | null
  corpses?: Corpse[]
  level: LevelConfig
  selectedTowerId: string | null
  selectedShrineMenuId?: string | null
  selectedHeroId: string | null
  selectedSummonId?: string | null
  ghostTower: { type: TowerType; position: Position; valid: boolean } | null
  isMenuOpen: boolean
  particleEffects: ParticleEffect[]
  onParticleComplete: (effectId: string) => void
  viewport: ViewportState
  landedPod: LandedPod | null
  onClick?: (e: React.MouseEvent<HTMLCanvasElement>) => void
  onRightClick?: (e: React.MouseEvent<HTMLCanvasElement>) => void
  selectedTilePosition?: Position | null
  isTargetingSkill?: boolean // Whether a skill is waiting for target selection
  pendingSkillInfo?: {
    type: SkillType
    summonType?: SummonType
    range?: number // skill range in tiles
  } | null
  mobileTargetPreviewPos?: Position | null // For mobile: position to show preview after first tap
  isTargetingItem?: boolean // Whether an item is waiting for target selection
  pendingItemInfo?: {
    effectType: ItemEffectType
    radius?: number // for lure effect radius
  } | null
  aoeVisualEffects?: AoeVisualEffect[]
  activeItemEffects?: ActiveItemEffect[]
  droppedItems?: DroppedItem[]
  isDroppodSelected?: boolean
  screenShakeOffset?: { x: number; y: number }
  cloneCount?: number
  showDroppodTutorial?: boolean
  upgradeModifiers?: UpgradeModifiers
  upgradeState?: UpgradeState
  abductionAnimation?: AbductionAnimationState | null
  soldierSpawnPortraits?: SoldierSpawnPortrait[]
  itemDropMessages?: ItemDropMessage[]
}

export function GameCanvas({
  enemies,
  towers,
  hero,
  projectiles,
  skillEffects = [],
  summonedUnits = [],
  rootsEffects = [],
  fireEffects = [],
  laserBeamEffects = [],
  barracksSoldiers = [],
  selectedSoldierId,
  selectedEnemyId,
  corpses = [],
  level,
  selectedTowerId,
  selectedShrineMenuId,
  selectedHeroId,
  selectedSummonId,
  ghostTower,
  isMenuOpen,
  particleEffects,
  onParticleComplete,
  viewport,
  landedPod,
  onClick,
  onRightClick,
  selectedTilePosition,
  isTargetingSkill = false,
  pendingSkillInfo,
  mobileTargetPreviewPos,
  isTargetingItem = false,
  pendingItemInfo,
  aoeVisualEffects = [],
  activeItemEffects = [],
  droppedItems = [],
  isDroppodSelected = false,
  screenShakeOffset = { x: 0, y: 0 },
  cloneCount = 0,
  showDroppodTutorial = false,
  upgradeModifiers,
  upgradeState,
  abductionAnimation = null,
  soldierSpawnPortraits = [],
  itemDropMessages = [],
}: GameCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [hoveredEnemy, setHoveredEnemy] = useState<Enemy | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null)
  const [mouseWorldPosition, setMouseWorldPosition] = useState<Position | null>(null)
  const landedPodImageRef = useRef<HTMLImageElement | null>(null)
  const itemSpriteImagesRef = useRef<Map<string, HTMLImageElement>>(new Map())
  // Ghost tower animation refs - persist animation start time across renders
  const ghostTowerAnimStartRef = useRef<number>(gameNow())
  const lastGhostTowerTypeRef = useRef<TowerType | null>(null)

  // Get viewport context for desktop detection
  const { isDesktop, isPillarboxed } = useViewport()
  // Track actual canvas dimensions for coordinate transforms
  const [canvasDimensions, setCanvasDimensions] = useState({ width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT })

  const renderWidth = canvasDimensions.width / viewport.zoom
  const renderHeight = canvasDimensions.height / viewport.zoom

  // Load landed pod image
  useEffect(() => {
    const img = new window.Image()
    img.src = "/images/droppod-landed.png"
    img.crossOrigin = "anonymous"
    landedPodImageRef.current = img
  }, [])

  // Load item images for dropped items
  useEffect(() => {
    // Collect all unique images needed from dropped items
    const imagesToLoad = new Set<string>()
    for (const item of droppedItems) {
      const itemDef = getItemDefinition(item.itemDefId)
      if (itemDef?.image) {
        imagesToLoad.add(itemDef.image)
      }
    }

    // Load any images not already cached
    for (const imagePath of imagesToLoad) {
      if (!itemSpriteImagesRef.current.has(imagePath)) {
        const img = new window.Image()
        img.src = imagePath
        img.crossOrigin = "anonymous"
        itemSpriteImagesRef.current.set(imagePath, img)
      }
    }
  }, [droppedItems])

  // Handle canvas resizing
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const resizeCanvas = () => {
      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight
      const isDesktopLandscape = isDesktop && isPillarboxed

      if (isDesktopLandscape) {
        // Full-width mode on desktop landscape
        const aspectRatio = screenWidth / screenHeight
        let internalWidth = Math.floor(VIEWPORT_HEIGHT * aspectRatio)

        // Cap internal width at WORLD_WIDTH to prevent showing empty space beyond the world
        // When internal width exceeds world width, pillarbox the canvas
        if (internalWidth > WORLD_WIDTH) {
          internalWidth = WORLD_WIDTH
          // Calculate CSS width that maintains aspect ratio with capped internal width
          const canvasDisplayWidth = screenHeight * (internalWidth / VIEWPORT_HEIGHT)
          canvas.style.width = `${canvasDisplayWidth}px`
        } else {
          canvas.style.width = `${screenWidth}px`
        }

        canvas.style.height = `${screenHeight}px`
        // Only touch the backing store when it actually changed - assigning
        // canvas.width always clears the bitmap (blank canvas while paused)
        if (canvas.width !== internalWidth || canvas.height !== VIEWPORT_HEIGHT) {
          canvas.width = internalWidth
          canvas.height = VIEWPORT_HEIGHT
        }
        setCanvasDimensions((prev) =>
          prev.width === internalWidth && prev.height === VIEWPORT_HEIGHT
            ? prev
            : { width: internalWidth, height: VIEWPORT_HEIGHT },
        )
      } else {
        // Original behavior: maintain aspect ratio with letterboxing
        const targetAspectRatio = VIEWPORT_WIDTH / VIEWPORT_HEIGHT
        const containerAspectRatio = screenWidth / screenHeight

        let scale: number
        if (containerAspectRatio > targetAspectRatio) {
          scale = screenHeight / VIEWPORT_HEIGHT
        } else {
          scale = screenWidth / VIEWPORT_WIDTH
        }

        canvas.style.width = `${VIEWPORT_WIDTH * scale}px`
        canvas.style.height = `${VIEWPORT_HEIGHT * scale}px`
        if (canvas.width !== VIEWPORT_WIDTH || canvas.height !== VIEWPORT_HEIGHT) {
          canvas.width = VIEWPORT_WIDTH
          canvas.height = VIEWPORT_HEIGHT
        }
        setCanvasDimensions((prev) =>
          prev.width === VIEWPORT_WIDTH && prev.height === VIEWPORT_HEIGHT
            ? prev
            : { width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT },
        )
      }
    }

    resizeCanvas()
    window.addEventListener("resize", resizeCanvas)
    window.addEventListener("orientationchange", resizeCanvas)

    return () => {
      window.removeEventListener("resize", resizeCanvas)
      window.removeEventListener("orientationchange", resizeCanvas)
    }
  }, [isDesktop, isPillarboxed])

  // Main render loop
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    ctx.save()

    // Apply screen shake offset
    ctx.translate(screenShakeOffset.x, screenShakeOffset.y)

    // Apply viewport transformation (zoom and pan)
    ctx.scale(viewport.zoom, viewport.zoom)
    ctx.translate(-viewport.x, -viewport.y)

    // World-space UI (health bars, status icons, selection brackets) is drawn
    // at a constant on-screen size once the player zooms past 1:1, so zooming
    // into a fight magnifies the battlefield and not the readouts. Below 1:1 it
    // is left alone, which keeps the default framing looking exactly as before.
    const overlayScale = Math.min(1, 1 / viewport.zoom)
    setOverlayScale(overlayScale)
    // Hairlines that should stay hairlines no matter the zoom
    const uiLineWidth = 1.5 * overlayScale

    // ===== PASS 1: TRUE BACKGROUND =====
    // Ground level layers - always behind entities
    const now = gameNow()
    const tmxViewport = { x: viewport.x, y: viewport.y, width: renderWidth, height: renderHeight }

    if (level.tmxMap) {
      // TMX true background: water, main space, shadows
      drawTMXMap(ctx, level.tmxMap, tmxViewport, now, "true_background")
    } else if (level.tilemap) {
      // Legacy tilemap rendering
      drawLevelTilemap(ctx, level.tilemap, viewport.x, viewport.y, renderWidth, renderHeight)
    } else {
      ctx.fillStyle = gameSettings.colors.background
      ctx.fillRect(viewport.x, viewport.y, renderWidth, renderHeight)
      // Draw path only if no tilemap (tilemap includes path tiles)
      drawPath(ctx, level.path, level.pathWidth)
    }

    // Draw base (on ground level)
    drawBase(ctx, level.basePosition, level.path)

    // Draw ghost tower preview with animated idle sprite
    // Reset animation start time when tower type changes
    if (ghostTower && ghostTower.type !== lastGhostTowerTypeRef.current) {
      ghostTowerAnimStartRef.current = gameNow()
      lastGhostTowerTypeRef.current = ghostTower.type
    } else if (!ghostTower) {
      lastGhostTowerTypeRef.current = null
    }

    if (ghostTower && ghostTower.valid && !isMenuOpen) {
      const config = towerTypes[ghostTower.type]
      const tile = worldToTile(ghostTower.position.x, ghostTower.position.y)

      ctx.save()
      ctx.globalAlpha = 0.7
      const ghostTowerObj: Tower = {
        id: "ghost",
        type: ghostTower.type,
        name: "",
        position: ghostTower.position,
        tileX: tile.tileX,
        tileY: tile.tileY,
        tileWidth: config.tileWidth,
        tileHeight: config.tileHeight,
        level: 1,
        damage: 0,
        range: 110,
        attackSpeed: 0,
        projectileSpeed: 0,
        attackCooldown: 0,
        rotation: 0,
        target: null,
        color: config.baseColor,
        attackType: config.attackType,
        health: 0,
        maxHealth: 0,
        animationState: "idle",
        animationStartTime: ghostTowerAnimStartRef.current,
      }
      drawTower(ctx, ghostTowerObj)
      ctx.restore()
    }

    // Towers are now drawn in the Y-sorted pass below for proper depth ordering

    // Draw corpses on the ground
    for (const corpse of corpses) {
      drawCorpse(ctx, corpse)
    }

    // Draw dropped items on the ground with god ray effect
    for (const droppedItem of droppedItems) {
      const itemDef = getItemDefinition(droppedItem.itemDefId)
      if (!itemDef) continue

      const itemSize = 24 // Size on canvas
      const x = droppedItem.position.x - itemSize / 2
      const y = droppedItem.position.y - itemSize / 2
      const centerX = droppedItem.position.x
      const centerY = droppedItem.position.y

      // Draw god ray effect behind the item
      ctx.save()
      const elapsed = now - droppedItem.createdAt
      const pulsePhase = (elapsed / 1000) % 1 // 1 second cycle
      const pulseIntensity = 0.4 + Math.sin(pulsePhase * Math.PI * 2) * 0.2
      const rayHeight = 60

      // Draw vertical light rays
      for (let i = 0; i < 3; i++) {
        const rayX = centerX + (i - 1) * 8
        const rayWidth = 12 * pulseIntensity
        const rayGradient = ctx.createLinearGradient(rayX, centerY - rayHeight, rayX, centerY + 10)
        rayGradient.addColorStop(0, `rgba(255, 255, 255, 0)`)
        rayGradient.addColorStop(0.3, `rgba(255, 223, 128, ${pulseIntensity * 0.4})`)
        rayGradient.addColorStop(0.7, `rgba(255, 215, 0, ${pulseIntensity * 0.6})`)
        rayGradient.addColorStop(1, `rgba(255, 200, 0, 0)`)

        ctx.fillStyle = rayGradient
        ctx.fillRect(rayX - rayWidth / 2, centerY - rayHeight, rayWidth, rayHeight + 20)
      }

      // Central glow under item
      ctx.beginPath()
      ctx.arc(centerX, centerY, 16 * pulseIntensity, 0, Math.PI * 2)
      const glowGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 16 * pulseIntensity)
      glowGradient.addColorStop(0, `rgba(255, 255, 255, ${pulseIntensity * 0.6})`)
      glowGradient.addColorStop(0.5, `rgba(255, 215, 0, ${pulseIntensity * 0.4})`)
      glowGradient.addColorStop(1, `rgba(255, 200, 0, 0)`)
      ctx.fillStyle = glowGradient
      ctx.fill()
      ctx.restore()

      // Draw the item image
      if (itemDef.image) {
        const itemImg = itemSpriteImagesRef.current.get(itemDef.image)
        if (itemImg?.complete) {
          ctx.save()
          ctx.imageSmoothingEnabled = false
          ctx.drawImage(itemImg, x, y, itemSize, itemSize)
          ctx.restore()
        } else {
          // Fallback to colored square while loading
          ctx.save()
          ctx.fillStyle = itemDef.iconColor
          ctx.fillRect(x, y, itemSize, itemSize)
          ctx.strokeStyle = "#fff"
          ctx.lineWidth = 2
          ctx.strokeRect(x, y, itemSize, itemSize)
          ctx.restore()
        }
      } else {
        // Fallback to colored square if no image defined
        ctx.save()
        ctx.fillStyle = itemDef.iconColor
        ctx.fillRect(x, y, itemSize, itemSize)
        ctx.strokeStyle = "#fff"
        ctx.lineWidth = 2
        ctx.strokeRect(x, y, itemSize, itemSize)
        ctx.restore()
      }
    }

    // Draw entangling roots effects on the ground
    for (const rootEffect of rootsEffects) {
      drawEntanglingRoots(ctx, rootEffect)
    }

    // ===== PASS 2: Y-SORTED ELEVATION + ENTITIES =====
    // Elevation tiles and entities are interleaved by Y position
    // This creates proper depth: entities behind elevated terrain are occluded

    // Collect all Y-sortable items
    type YSortedItem = { sortY: number; render: () => void }
    const sortedItems: YSortedItem[] = []

    // Id -> tower lookup, built once per frame (soldiers resolve their barracks)
    const towerById = new Map(towers.map((t) => [t.id, t]))

    // Calculate tower opacity during abduction (towers fade out with hero)
    let towerOpacity = 1
    if (abductionAnimation) {
      if (abductionAnimation.phase === "hero_ascend") {
        const elapsed = now - abductionAnimation.phaseStartTime
        const progress = Math.min(elapsed / 2000, 1)
        towerOpacity = 1 - progress // Fade out with hero
      } else if (["beam_fade_out", "score_count", "score_display"].includes(abductionAnimation.phase)) {
        towerOpacity = 0 // Keep hidden after ascend
      }
    }

    // Add towers to sort list (so they render in proper depth order with foreground)
    for (const tower of towers) {
      // Use bottom of tower position for Y-sorting
      const footY = tower.position.y + 16 // Bottom of tower tile
      const isSelected = selectedTowerId === tower.id
      sortedItems.push({
        sortY: footY,
        render: () => {
          // Skip rendering if fully faded during abduction
          if (towerOpacity <= 0) return

          ctx.save()
          ctx.globalAlpha = towerOpacity

          // Draw range indicator circle when tower is selected
          // Only show for archer, mage, and siege towers (towers with attack range)
          if (isSelected && (tower.type === "archer" || tower.type === "mage" || tower.type === "siege" || tower.type === "shadowblade")) {
            // Apply range modifier for archer towers
            const rangeModifier = tower.type === "archer" && upgradeModifiers ? upgradeModifiers.archerRange : 1
            const effectiveRange = tower.range * rangeModifier

            // Draw subtle radar sweep effect (rotating wedge)
            const sweepAngle = (now / 8000) * Math.PI * 2 // Full rotation every 8 seconds
            const sweepWidth = Math.PI / 3 // 60 degree sweep
            const gradient = ctx.createConicGradient(sweepAngle - sweepWidth, tower.position.x, tower.position.y)
            gradient.addColorStop(0, "rgba(255, 255, 255, 0)")
            gradient.addColorStop(0.5, "rgba(255, 255, 255, 0.08)")
            gradient.addColorStop(1, "rgba(255, 255, 255, 0)")

            ctx.beginPath()
            ctx.moveTo(tower.position.x, tower.position.y)
            ctx.arc(tower.position.x, tower.position.y, effectiveRange, sweepAngle - sweepWidth, sweepAngle)
            ctx.closePath()
            ctx.fillStyle = gradient
            ctx.fill()

            // Draw range circle border (white for normal, green if upgraded)
            ctx.beginPath()
            ctx.arc(tower.position.x, tower.position.y, effectiveRange, 0, Math.PI * 2)
            ctx.strokeStyle = rangeModifier > 1 ? "rgba(34, 197, 94, 0.4)" : "rgba(255, 255, 255, 0.3)"
            ctx.lineWidth = uiLineWidth
            ctx.stroke()
          }

          // Draw shrine effect radius when shrine is selected or menu is open
          const isShrineMenuOpen = selectedShrineMenuId === tower.id
          if ((isSelected || isShrineMenuOpen) && tower.type === "shrine") {
            // Calculate shrine effect radius using shrine system
            const effectRadius = getShrineEffectRadius(tower.level)

            // Draw radar sweep effect (similar to tower range indicator)
            const sweepAngle = (now / 8000) * Math.PI * 2 // Full rotation every 8 seconds
            const sweepWidth = Math.PI / 3 // 60 degree sweep
            const gradient = ctx.createConicGradient(sweepAngle - sweepWidth, tower.position.x, tower.position.y)
            gradient.addColorStop(0, "rgba(99, 102, 241, 0)") // Indigo
            gradient.addColorStop(0.5, "rgba(99, 102, 241, 0.08)")
            gradient.addColorStop(1, "rgba(99, 102, 241, 0)")

            ctx.beginPath()
            ctx.moveTo(tower.position.x, tower.position.y)
            ctx.arc(tower.position.x, tower.position.y, effectRadius, sweepAngle - sweepWidth, sweepAngle)
            ctx.closePath()
            ctx.fillStyle = gradient
            ctx.fill()

            // Draw radius border
            ctx.beginPath()
            ctx.arc(tower.position.x, tower.position.y, effectRadius, 0, Math.PI * 2)
            ctx.strokeStyle = "rgba(99, 102, 241, 0.3)" // Indigo
            ctx.lineWidth = uiLineWidth
            ctx.stroke()
          }
          drawTower(ctx, tower)
          // Draw health bar only if tower is damaged
          if (tower.health < tower.maxHealth) {
            // Tower sprite size varies, use a reasonable estimate for health bar positioning
            const towerSize = tower.type === "barracks" ? 64 : 48
            drawHealthBar(ctx, tower.position, tower.health, tower.maxHealth, towerSize)
          }
          ctx.restore()
        },
      })
    }

    // Add enemies to sort list
    for (const enemy of enemies) {
      const footY = enemy.position.y + 24 // Bottom of enemy sprite
      // Boost recently damaged enemies to render on top (visible health bars)
      const recentlyDamaged = now - (enemy.lastDamageTime || 0) < 2000
      const damageBoost = recentlyDamaged ? 10000 : 0
      const isEnemySelected = selectedEnemyId === enemy.id
      sortedItems.push({
        sortY: footY + damageBoost,
        render: () => {
          // Draw aggro radius indicator for selected enemy
          if (isEnemySelected) {
            ctx.save()
            ctx.beginPath()
            ctx.arc(enemy.position.x, enemy.position.y, enemy.aggroRadius, 0, Math.PI * 2)
            ctx.strokeStyle = enemy.aggroTargetPosition
              ? "rgba(255, 80, 80, 0.5)" // Red when actively chasing
              : "rgba(200, 200, 200, 0.4)" // Gray when passive
            ctx.lineWidth = uiLineWidth
            ctx.setLineDash([4 * overlayScale, 4 * overlayScale])
            ctx.stroke()
            ctx.setLineDash([])
            ctx.restore()
          }
          drawEnemy(ctx, enemy)
          if (enemy.slowMultiplier < 1.0 && enemy.slowMultiplier > 0) {
            drawSlowEffect(ctx, enemy)
          }
          drawRootEffect(ctx, enemy)
        },
      })
    }

    // Add summoned units to sort list
    for (const summon of summonedUnits) {
      if (summon.health <= 0) continue
      const footY = summon.position.y + 24
      const isSelected = selectedSummonId === summon.id
      sortedItems.push({
        sortY: footY,
        render: () => {
          if (summon.type === "wolf") {
            drawSummonedWolf(ctx, summon, isSelected)
          } else if (summon.type === "fox") {
            drawSummonedFox(ctx, summon, isSelected)
          } else if (summon.type === "boar") {
            drawSummonedBoar(ctx, summon, isSelected)
          } else if (summon.type === "siege-tower") {
            drawSummonedTower(ctx, summon, isSelected)
          }
        },
      })
    }

    // Add barracks soldiers to sort list (skip if fully faded during abduction)
    if (towerOpacity > 0) {
      for (const soldier of barracksSoldiers) {
        if (soldier.isDead) continue
        const footY = soldier.position.y + 16
        // Find the barracks tower for this soldier to get its level
        const barracksTower = towerById.get(soldier.barracksTowerId)
        const barracksLevel = barracksTower?.level ?? 1
        const isSelected = soldier.id === selectedSoldierId
        sortedItems.push({
          sortY: footY,
          render: () => {
            ctx.save()
            ctx.globalAlpha = towerOpacity
            drawBarracksSoldier(ctx, soldier, barracksLevel, isSelected, upgradeState)
            ctx.restore()
          },
        })
      }
    }

    // Add landed pod to sort list (slight Y offset ensures hero renders in front when close)
    if (landedPod && landedPod.position && landedPodImageRef.current?.complete) {
      const podSize = 64
      const footY = landedPod.position.y + podSize / 2 - 1
      sortedItems.push({
        sortY: footY,
        render: () => {
          // Draw healing aura circle when droppod is selected
          if (isDroppodSelected) {
            const HEAL_AURA_RADIUS = 80
            ctx.save()
            ctx.beginPath()
            ctx.arc(landedPod.position.x, landedPod.position.y, HEAL_AURA_RADIUS, 0, Math.PI * 2)
            ctx.fillStyle = "rgba(50, 200, 80, 0.15)"
            ctx.fill()
            ctx.strokeStyle = "rgba(50, 200, 80, 0.5)"
            ctx.lineWidth = 2
            ctx.stroke()
            ctx.restore()
          }

          ctx.drawImage(
            landedPodImageRef.current!,
            landedPod.position.x - podSize / 2,
            landedPod.position.y - podSize / 2,
            podSize,
            podSize,
          )

          // Draw smoke particles rising from top of droppod
          ctx.save()
          const smokeBaseX = landedPod.position.x
          const smokeBaseY = landedPod.position.y - podSize / 2 - 5 // Just above the pod
          const smokeParticleCount = 6
          const smokeTime = now / 1000 // Time in seconds

          for (let i = 0; i < smokeParticleCount; i++) {
            // Each particle has its own cycle offset
            const cycleOffset = i / smokeParticleCount
            const particleTime = (smokeTime + cycleOffset * 2) % 2 // 2 second cycle per particle
            const progress = particleTime / 2 // 0 to 1 over the cycle

            // Particle rises and drifts
            const riseHeight = progress * 40
            const drift = Math.sin((progress + i) * Math.PI * 2) * 8
            const px = smokeBaseX + drift + (i % 2 === 0 ? -3 : 3)
            const py = smokeBaseY - riseHeight

            // Particle grows then shrinks
            const sizeCurve = Math.sin(progress * Math.PI)
            const size = 3 + sizeCurve * 5

            // Fade out as it rises
            const opacity = (1 - progress) * 0.4

            ctx.beginPath()
            ctx.arc(px, py, size, 0, Math.PI * 2)
            ctx.fillStyle = `rgba(180, 180, 180, ${opacity})`
            ctx.fill()
          }
          ctx.restore()
        },
      })
    }

    // Add hero to sort list
    if (hero && hero.position) {
      // Calculate abduction animation offsets
      let heroYOffset = 0
      let heroOpacity = 1

      if (abductionAnimation?.phase === "hero_ascend") {
        const elapsed = now - abductionAnimation.phaseStartTime
        const progress = Math.min(elapsed / 2000, 1) // 2 second ascend
        // Ease-in: start slow, accelerate (quadratic)
        const easedProgress = progress * progress
        heroYOffset = -easedProgress * 300 // Rise 300 pixels
        heroOpacity = 1 - progress // Fade out linearly
      } else if (abductionAnimation && ["beam_fade_out", "score_count", "score_display"].includes(abductionAnimation.phase)) {
        // Keep hero hidden after ascend completes
        heroOpacity = 0
      }

      const footY = hero.position.y + heroYOffset + 24
      sortedItems.push({
        sortY: footY,
        render: () => {
          if (heroOpacity <= 0) return
          ctx.save()
          ctx.globalAlpha = heroOpacity
          // Create adjusted hero with offset position
          const adjustedHero = {
            ...hero,
            position: { x: hero.position.x, y: hero.position.y + heroYOffset }
          }
          drawHero(ctx, adjustedHero, selectedHeroId === hero.id, activeItemEffects)
          ctx.restore()
        },
      })
    }

    // Add elevation tile rows to sort list (if TMX map exists)
    if (level.tmxMap) {
      // Create tile rows covering the viewport
      const startY = Math.floor(viewport.y / TILE_SIZE) * TILE_SIZE
      const endY = viewport.y + renderHeight + TILE_SIZE
      for (let rowY = startY; rowY < endY; rowY += TILE_SIZE) {
        const yMin = rowY
        const yMax = rowY + TILE_SIZE
        sortedItems.push({
          sortY: yMax, // Bottom edge of tile row
          render: () => {
            drawTMXElevationRow(ctx, level.tmxMap!, tmxViewport, yMin, yMax, now)
          },
        })
      }
    }

    // Sort by Y (lower Y = behind, higher Y = in front)
    sortedItems.sort((a, b) => a.sortY - b.sortY)

    // Draw level-up effects BEFORE sorted items (so they appear behind the hero)
    for (const effect of particleEffects) {
      if (effect.type !== "level-up") continue
      const elapsed = now - effect.timestamp
      const duration = 1200
      if (elapsed >= duration) continue

      const progress = elapsed / duration
      const fadeIn = Math.min(1, progress * 5) // Quick fade in (first 20% of duration)
      const fadeOut = progress > 0.7 ? 1 - (progress - 0.7) / 0.3 : 1 // Fade out last 30%
      const effectOpacity = fadeIn * fadeOut

      const rayWidth = 12 // Narrow
      const rayHeight = 200 // Tall
      // Position 10px lower (more south) on Y axis
      const effectY = effect.position.y + 10

      ctx.save()

      // Draw single central ray
      const rayGradient = ctx.createLinearGradient(
        effect.position.x, effectY - rayHeight,
        effect.position.x, effectY + 30
      )
      rayGradient.addColorStop(0, `rgba(255, 255, 255, 0)`)
      rayGradient.addColorStop(0.2, `rgba(255, 255, 255, ${effectOpacity * 0.4})`)
      rayGradient.addColorStop(0.5, `rgba(255, 255, 255, ${effectOpacity * 0.9})`)
      rayGradient.addColorStop(0.8, `rgba(240, 248, 255, ${effectOpacity * 0.6})`)
      rayGradient.addColorStop(1, `rgba(255, 255, 255, 0)`)

      ctx.fillStyle = rayGradient
      ctx.fillRect(effect.position.x - rayWidth / 2, effectY - rayHeight, rayWidth, rayHeight + 50)

      // Soft outer glow ray (wider, more transparent)
      const outerRayWidth = 24
      const outerGradient = ctx.createLinearGradient(
        effect.position.x, effectY - rayHeight,
        effect.position.x, effectY + 30
      )
      outerGradient.addColorStop(0, `rgba(255, 255, 255, 0)`)
      outerGradient.addColorStop(0.3, `rgba(240, 248, 255, ${effectOpacity * 0.2})`)
      outerGradient.addColorStop(0.6, `rgba(255, 255, 255, ${effectOpacity * 0.3})`)
      outerGradient.addColorStop(1, `rgba(255, 255, 255, 0)`)

      ctx.fillStyle = outerGradient
      ctx.fillRect(effect.position.x - outerRayWidth / 2, effectY - rayHeight, outerRayWidth, rayHeight + 50)

      // Small central glow at hero position
      const glowSize = 15 * effectOpacity
      const centerGlow = ctx.createRadialGradient(
        effect.position.x, effectY, 0,
        effect.position.x, effectY, glowSize
      )
      centerGlow.addColorStop(0, `rgba(255, 255, 255, ${effectOpacity * 0.8})`)
      centerGlow.addColorStop(0.5, `rgba(240, 248, 255, ${effectOpacity * 0.4})`)
      centerGlow.addColorStop(1, `rgba(255, 255, 255, 0)`)
      ctx.beginPath()
      ctx.arc(effect.position.x, effectY, glowSize, 0, Math.PI * 2)
      ctx.fillStyle = centerGlow
      ctx.fill()

      ctx.restore()
    }

    // Draw abduction beam BEFORE sorted items (so it appears behind the hero)
    if (abductionAnimation && hero && hero.position) {
      let beamOpacity = 0

      if (abductionAnimation.phase === "beam_fade_in") {
        const elapsed = now - abductionAnimation.phaseStartTime
        beamOpacity = Math.min(elapsed / 1000, 1) // 1 second fade in
      } else if (abductionAnimation.phase === "hero_ascend") {
        beamOpacity = 1
      } else if (abductionAnimation.phase === "beam_fade_out") {
        const elapsed = now - abductionAnimation.phaseStartTime
        beamOpacity = 1 - Math.min(elapsed / 500, 1) // 0.5 second fade out
      }

      if (beamOpacity > 0) {
        // Calculate hero Y offset for beam target during ascend
        let heroYOffset = 0
        if (abductionAnimation.phase === "hero_ascend") {
          const elapsed = now - abductionAnimation.phaseStartTime
          const progress = Math.min(elapsed / 2000, 1)
          const easedProgress = progress * progress
          heroYOffset = -easedProgress * 300
        }

        const heroPos = { x: hero.position.x, y: hero.position.y + heroYOffset + 10 } // +10px south
        const beamTopY = viewport.y - 50 // Start above viewport
        drawAbductionBeam(ctx, heroPos, beamTopY, beamOpacity)
      }
    }

    // Render all Y-sorted items
    for (const item of sortedItems) {
      item.render()
    }

    // Draw projectiles (above entities, but still affected by foreground)
    for (const projectile of projectiles) {
      drawProjectile(ctx, projectile)
    }

    // Draw fire ground effects from siege towers (above tiles/enemies/projectiles)
    for (const fireEffect of fireEffects) {
      drawFireGroundEffect(ctx, fireEffect)
    }

    // Draw laser beams from forge turrets
    for (const laser of laserBeamEffects) {
      if (!laser.isActive) continue
      drawLaserBeam(ctx, laser.sourcePosition, laser.targetPosition)
    }

    // ===== PASS 3: TRUE FOREGROUND =====
    // Canopies and overhead objects - always above entities
    if (level.tmxMap) {
      drawTMXMap(ctx, level.tmxMap, tmxViewport, now, "true_foreground")
    }

    // Draw rally points for selected barracks tower (above foreground)
    const selectedTower = selectedTowerId ? towers.find((t) => t.id === selectedTowerId) : null
    if (selectedTower?.type === "barracks" && selectedTower.barracksState) {
      // Draw rally radius circle
      const rallyRadius = getRallyRadius()
      ctx.save()

      // Draw subtle radar sweep effect (rotating wedge) - similar to tower range
      const sweepAngle = (now / 2000) * Math.PI * 2 // Full rotation every 2 seconds
      const sweepWidth = Math.PI / 3 // 60 degree sweep
      const gradient = ctx.createConicGradient(sweepAngle - sweepWidth, selectedTower.position.x, selectedTower.position.y)
      gradient.addColorStop(0, "rgba(255, 255, 255, 0)")
      gradient.addColorStop(0.5, "rgba(255, 255, 255, 0.08)")
      gradient.addColorStop(1, "rgba(255, 255, 255, 0)")

      ctx.beginPath()
      ctx.moveTo(selectedTower.position.x, selectedTower.position.y)
      ctx.arc(selectedTower.position.x, selectedTower.position.y, rallyRadius, sweepAngle - sweepWidth, sweepAngle)
      ctx.closePath()
      ctx.fillStyle = gradient
      ctx.fill()

      // Draw rally radius border (white semi-transparent)
      ctx.beginPath()
      ctx.arc(selectedTower.position.x, selectedTower.position.y, rallyRadius, 0, Math.PI * 2)
      ctx.strokeStyle = "rgba(255, 255, 255, 0.3)"
      ctx.lineWidth = uiLineWidth
      ctx.stroke()
      ctx.restore()

      // Draw the rally point flag
      drawRallyPoint(ctx, selectedTower.barracksState.rallyPoint, true)
    }

    // Draw respawn bars for dead soldiers under their barracks towers (above foreground)
    // Skip during abduction sequence (fade out and hide with towers)
    if (!abductionAnimation) {
      // Running per-barracks counter replaces the O(dead^2) filter+indexOf
      const deadBarIndex = new Map<string, number>()
      for (const soldier of barracksSoldiers) {
        if (!soldier.isDead) continue
        const barracksTower = towerById.get(soldier.barracksTowerId)
        if (!barracksTower) continue

        const progress = getSoldierRespawnProgress(soldier)
        const barIndex = deadBarIndex.get(soldier.barracksTowerId) ?? 0
        deadBarIndex.set(soldier.barracksTowerId, barIndex + 1)
        drawSoldierRespawnBar(ctx, barracksTower.position, progress, barIndex)
      }
    }

    // Draw soldier spawn portraits (floating up from barracks)
    for (const portrait of soldierSpawnPortraits) {
      drawSoldierSpawnPortrait(
        ctx,
        portrait.towerPosition,
        portrait.barrackLevel,
        portrait.startTime,
      )
    }

    // Draw skill effects
    for (const effect of skillEffects) {
      drawSkillEffect(ctx, effect)
    }

    // Draw AoE visual effects (expanding circle when abilities like Tremor Slam are used)
    // Multi-wave effects render with different intensities, all travel the same distance
    for (const aoeEffect of aoeVisualEffects) {
      ctx.save()

      // Calculate progress (1.0 = just started, 0.0 = about to end)
      const progress = aoeEffect.remainingFrames / aoeEffect.duration
      const intensity = aoeEffect.intensity ?? 1.0

      // Waves expand from center to full radius (all travel the same distance)
      // progress goes from 1.0 to 0.0, so (1 - progress) goes from 0.0 to 1.0
      const expansionProgress = 1 - progress
      const currentRadius = aoeEffect.radius * expansionProgress

      // Fade out as the wave expands (peak opacity at ~30% expansion)
      const opacityFalloff = expansionProgress < 0.3
        ? expansionProgress / 0.3 // Fade in
        : 1 - (expansionProgress - 0.3) / 0.7 // Fade out
      const baseOpacity = opacityFalloff * 0.5 * intensity

      // Skip if radius is too small to see
      if (currentRadius < 5) {
        ctx.restore()
        continue
      }

      // Draw expanding ring (not filled circle - just the wavefront)
      const ringWidth = 15 + (aoeEffect.waveIndex === 0 ? 10 : 0) // First wave is thicker
      const innerRadius = Math.max(0, currentRadius - ringWidth)

      // First wave (waveIndex 0) gets a more intense glow
      if (aoeEffect.waveIndex === 0) {
        // Draw intense ring for first wave
        const gradient = ctx.createRadialGradient(
          aoeEffect.position.x, aoeEffect.position.y, innerRadius,
          aoeEffect.position.x, aoeEffect.position.y, currentRadius,
        )
        gradient.addColorStop(0, `rgba(255, 255, 255, 0)`)
        gradient.addColorStop(0.3, `rgba(255, 255, 255, ${baseOpacity * 0.8})`)
        gradient.addColorStop(0.7, `rgba(255, 255, 255, ${baseOpacity})`)
        gradient.addColorStop(1, `rgba(255, 255, 255, ${baseOpacity * 0.3})`)
        ctx.beginPath()
        ctx.arc(aoeEffect.position.x, aoeEffect.position.y, currentRadius, 0, Math.PI * 2)
        ctx.arc(aoeEffect.position.x, aoeEffect.position.y, innerRadius, 0, Math.PI * 2, true)
        ctx.fillStyle = gradient
        ctx.fill()

        // Draw bright edge stroke
        ctx.beginPath()
        ctx.arc(aoeEffect.position.x, aoeEffect.position.y, currentRadius, 0, Math.PI * 2)
        ctx.strokeStyle = `rgba(255, 255, 255, ${baseOpacity * 1.5})`
        ctx.lineWidth = 3
        ctx.stroke()
      } else {
        // Subsequent waves are more subtle rings
        ctx.beginPath()
        ctx.arc(aoeEffect.position.x, aoeEffect.position.y, currentRadius, 0, Math.PI * 2)
        ctx.arc(aoeEffect.position.x, aoeEffect.position.y, innerRadius, 0, Math.PI * 2, true)
        ctx.fillStyle = `rgba(255, 255, 255, ${baseOpacity * 0.5})`
        ctx.fill()
        ctx.beginPath()
        ctx.arc(aoeEffect.position.x, aoeEffect.position.y, currentRadius, 0, Math.PI * 2)
        ctx.strokeStyle = `rgba(255, 255, 255, ${baseOpacity})`
        ctx.lineWidth = 2
        ctx.stroke()
      }

      ctx.restore()
    }

    // Draw particle effects (item usage, equipment, etc.)
    for (const effect of particleEffects) {
      // Skip level-up effects - they are rendered earlier (behind hero)
      if (effect.type === "level-up") {
        const elapsed = now - effect.timestamp
        if (elapsed >= 1200) {
          onParticleComplete(effect.id)
        }
        continue
      }

      const elapsed = now - effect.timestamp
      const isGoldCoin = effect.type === "gold-coin"
      const isGodRay = effect.type === "god-ray"
      const isCloneRestore = effect.type === "clone-restore"
      const isLifestealDrain = effect.type === "lifesteal-drain"
      const isLifestealHeal = effect.type === "lifesteal-heal"
      const isSoldierUpgrade = effect.type === "soldier-upgrade"
      const isTowerCollapse = effect.type === "tower-collapse"
      const isSporeCloud = effect.type === "spore-cloud"
      const isToxicBurst = effect.type === "toxic-burst"
      const duration = isGoldCoin ? 1000 : isGodRay ? 3000 : isSoldierUpgrade ? 2000 : isTowerCollapse ? 800 : isCloneRestore ? 1500 : isLifestealDrain ? 600 : isLifestealHeal ? 500 : isSporeCloud ? 1200 : isToxicBurst ? 600 : 800

      if (elapsed >= duration) {
        // Mark for removal
        onParticleComplete(effect.id)
        continue
      }

      const progress = elapsed / duration
      const opacity = 1 - progress // Fade out over time

      ctx.save()

      if (isGoldCoin) {
        // Gold coin particle effect - floating coin with "+X" text
        const rise = progress * 50 // Float upward
        const wobble = Math.sin(progress * Math.PI * 4) * 3 // Side-to-side wobble

        // Draw gold coin circle
        const coinX = effect.position.x + wobble
        const coinY = effect.position.y - rise - 10
        const coinSize = 8 * (1 - progress * 0.3)

        // Coin glow
        ctx.beginPath()
        ctx.arc(coinX, coinY, coinSize + 2, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(255, 215, 0, ${opacity * 0.3})`
        ctx.fill()

        // Coin body
        ctx.beginPath()
        ctx.arc(coinX, coinY, coinSize, 0, Math.PI * 2)
        const coinGradient = ctx.createRadialGradient(coinX - 2, coinY - 2, 0, coinX, coinY, coinSize)
        coinGradient.addColorStop(0, "#FFEC8B")
        coinGradient.addColorStop(0.5, "#FFD700")
        coinGradient.addColorStop(1, "#DAA520")
        ctx.fillStyle = coinGradient
        ctx.globalAlpha = opacity
        ctx.fill()

        // Draw "+X" text
        ctx.font = "bold 12px sans-serif"
        ctx.textAlign = "center"
        ctx.textBaseline = "bottom"
        ctx.fillStyle = "#FFD700"
        ctx.globalAlpha = opacity
        ctx.strokeStyle = "rgba(0, 0, 0, 0.8)"
        ctx.lineWidth = 2
        const goldText = `+${effect.value || 1}`
        ctx.strokeText(goldText, effect.position.x, coinY - coinSize - 2)
        ctx.fillText(goldText, effect.position.x, coinY - coinSize - 2)
      } else if (isGodRay) {
        // God ray effect for boss drops - vertical beams of light
        const pulseIntensity = 0.5 + Math.sin(progress * Math.PI * 6) * 0.3
        const rayWidth = 30 * pulseIntensity
        const rayHeight = 150 * (1 - progress * 0.5)

        // Draw multiple rays
        for (let i = 0; i < 5; i++) {
          const rayX = effect.position.x + (i - 2) * 12
          const rayGradient = ctx.createLinearGradient(rayX, effect.position.y - rayHeight, rayX, effect.position.y + 20)
          rayGradient.addColorStop(0, `rgba(255, 255, 255, 0)`)
          rayGradient.addColorStop(0.3, `rgba(255, 223, 128, ${opacity * pulseIntensity * 0.6})`)
          rayGradient.addColorStop(0.7, `rgba(255, 215, 0, ${opacity * pulseIntensity * 0.8})`)
          rayGradient.addColorStop(1, `rgba(255, 200, 0, 0)`)

          ctx.fillStyle = rayGradient
          ctx.fillRect(rayX - rayWidth / 2, effect.position.y - rayHeight, rayWidth, rayHeight + 40)
        }

        // Central glow
        ctx.beginPath()
        ctx.arc(effect.position.x, effect.position.y, 25 * pulseIntensity, 0, Math.PI * 2)
        const glowGradient = ctx.createRadialGradient(effect.position.x, effect.position.y, 0, effect.position.x, effect.position.y, 25 * pulseIntensity)
        glowGradient.addColorStop(0, `rgba(255, 255, 255, ${opacity * 0.8})`)
        glowGradient.addColorStop(0.5, `rgba(255, 215, 0, ${opacity * 0.5})`)
        glowGradient.addColorStop(1, `rgba(255, 200, 0, 0)`)
        ctx.fillStyle = glowGradient
        ctx.fill()
      } else if (effect.type === "clone-restore") {
        // Clone restore effect - blue upward traveling particles
        const rise = progress * 80 // Float upward
        const wobble = Math.sin(progress * Math.PI * 3) * 5 // Gentle side-to-side

        const px = effect.position.x + wobble
        const py = effect.position.y - rise
        const particleSize = 6 * (1 - progress * 0.5)

        // Outer glow
        ctx.beginPath()
        ctx.arc(px, py, particleSize + 4, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(100, 180, 255, ${opacity * 0.3})`
        ctx.fill()

        // Inner particle
        ctx.beginPath()
        ctx.arc(px, py, particleSize, 0, Math.PI * 2)
        const blueGradient = ctx.createRadialGradient(px - 1, py - 1, 0, px, py, particleSize)
        blueGradient.addColorStop(0, "#FFFFFF")
        blueGradient.addColorStop(0.4, "#80C8FF")
        blueGradient.addColorStop(1, "#4090FF")
        ctx.fillStyle = blueGradient
        ctx.globalAlpha = opacity
        ctx.fill()
      } else if (effect.type === "lifesteal-drain") {
        // Red particles rising from enemy (life being drained)
        const particleCount = 8

        for (let i = 0; i < particleCount; i++) {
          const angle = (i / particleCount) * Math.PI * 2 + progress * Math.PI
          const spread = 15 + progress * 20
          const riseAmount = progress * 40

          const px = effect.position.x + Math.cos(angle) * spread
          const py = effect.position.y - riseAmount + Math.sin(angle) * 5

          const particleSize = 4 * (1 - progress * 0.5)
          const alpha = opacity * (1 - progress * 0.3)

          ctx.beginPath()
          ctx.arc(px, py, particleSize, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(220, 38, 38, ${alpha})`
          ctx.fill()

          // Glow effect
          const gradient = ctx.createRadialGradient(px, py, 0, px, py, particleSize * 2)
          gradient.addColorStop(0, `rgba(239, 68, 68, ${alpha * 0.5})`)
          gradient.addColorStop(1, "rgba(239, 68, 68, 0)")
          ctx.beginPath()
          ctx.arc(px, py, particleSize * 2, 0, Math.PI * 2)
          ctx.fillStyle = gradient
          ctx.fill()
        }
      } else if (effect.type === "lifesteal-heal") {
        // Red particles converging toward hero center (life being absorbed)
        const particleCount = 12

        // Particles start from outer ring and converge to center
        for (let i = 0; i < particleCount; i++) {
          const angle = (i / particleCount) * Math.PI * 2
          const startRadius = 35
          const currentRadius = startRadius * (1 - progress)

          const px = effect.position.x + Math.cos(angle) * currentRadius
          const py = effect.position.y + Math.sin(angle) * currentRadius

          const particleSize = 3 + progress * 2
          const alpha = opacity * (0.8 + progress * 0.2)

          ctx.beginPath()
          ctx.arc(px, py, particleSize, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(220, 38, 38, ${alpha})`
          ctx.fill()

          // Trail effect
          const trailLength = 3
          for (let t = 1; t <= trailLength; t++) {
            const trailRadius = currentRadius + t * 5
            const trailPx = effect.position.x + Math.cos(angle) * trailRadius
            const trailPy = effect.position.y + Math.sin(angle) * trailRadius
            const trailAlpha = alpha * (1 - t / (trailLength + 1))

            ctx.beginPath()
            ctx.arc(trailPx, trailPy, particleSize * 0.6, 0, Math.PI * 2)
            ctx.fillStyle = `rgba(185, 28, 28, ${trailAlpha})`
            ctx.fill()
          }
        }

        // Central glow that intensifies as particles converge
        const glowRadius = 20 * (0.5 + progress * 0.5)
        const glowGradient = ctx.createRadialGradient(
          effect.position.x, effect.position.y, 0,
          effect.position.x, effect.position.y, glowRadius
        )
        glowGradient.addColorStop(0, `rgba(220, 38, 38, ${progress * 0.6})`)
        glowGradient.addColorStop(0.5, `rgba(185, 28, 28, ${progress * 0.3})`)
        glowGradient.addColorStop(1, "rgba(185, 28, 28, 0)")
        ctx.beginPath()
        ctx.arc(effect.position.x, effect.position.y, glowRadius, 0, Math.PI * 2)
        ctx.fillStyle = glowGradient
        ctx.fill()
      } else if (effect.type === "soldier-upgrade") {
        // Soldier upgrade god ray effect - 60% size of hero god ray, scaled by soldier size
        const baseScale = 0.6 * (effect.scale || 1) // 60% base size, further scaled by soldier level
        const pulseIntensity = 0.5 + Math.sin(progress * Math.PI * 6) * 0.3
        const rayWidth = 30 * baseScale * pulseIntensity
        const rayHeight = 150 * baseScale * (1 - progress * 0.5)

        // Draw multiple rays
        for (let i = 0; i < 5; i++) {
          const rayX = effect.position.x + (i - 2) * 12 * baseScale
          const rayGradient = ctx.createLinearGradient(rayX, effect.position.y - rayHeight, rayX, effect.position.y + 20 * baseScale)
          rayGradient.addColorStop(0, `rgba(255, 255, 255, 0)`)
          rayGradient.addColorStop(0.3, `rgba(255, 200, 100, ${opacity * pulseIntensity * 0.6})`) // Amber tint for soldiers
          rayGradient.addColorStop(0.7, `rgba(255, 180, 80, ${opacity * pulseIntensity * 0.8})`)
          rayGradient.addColorStop(1, `rgba(255, 160, 60, 0)`)

          ctx.fillStyle = rayGradient
          ctx.fillRect(rayX - rayWidth / 2, effect.position.y - rayHeight, rayWidth, rayHeight + 40 * baseScale)
        }

        // Central glow (amber colored)
        const glowSize = 25 * baseScale * pulseIntensity
        ctx.beginPath()
        ctx.arc(effect.position.x, effect.position.y, glowSize, 0, Math.PI * 2)
        const soldierGlowGradient = ctx.createRadialGradient(effect.position.x, effect.position.y, 0, effect.position.x, effect.position.y, glowSize)
        soldierGlowGradient.addColorStop(0, `rgba(255, 255, 255, ${opacity * 0.8})`)
        soldierGlowGradient.addColorStop(0.5, `rgba(255, 180, 80, ${opacity * 0.5})`)
        soldierGlowGradient.addColorStop(1, `rgba(255, 160, 60, 0)`)
        ctx.fillStyle = soldierGlowGradient
        ctx.fill()
      } else if (effect.type === "tower-collapse") {
        // Tower collapse dust cloud effect - gray/brown particles expanding outward
        const particleCount = 16
        const baseRadius = 20
        const expandSpeed = 60 // How far particles travel

        for (let i = 0; i < particleCount; i++) {
          const angle = (i / particleCount) * Math.PI * 2
          const randomOffset = Math.sin(i * 7.3) * 0.3 // Pseudo-random variation
          const distance = baseRadius + progress * expandSpeed * (0.7 + randomOffset)
          const riseAmount = progress * 30 * (0.5 + randomOffset) // Rise upward with variation

          const px = effect.position.x + Math.cos(angle) * distance
          const py = effect.position.y - riseAmount + Math.sin(angle) * distance * 0.3

          // Particle size shrinks over time
          const particleSize = (8 + i % 4) * (1 - progress * 0.6)
          const alpha = opacity * (1 - progress * 0.5)

          // Use colors from the effect (gray/brown tones)
          const colorIndex = i % effect.colors.length
          const color = effect.colors[colorIndex]

          // Draw dust particle
          ctx.beginPath()
          ctx.arc(px, py, particleSize, 0, Math.PI * 2)
          ctx.fillStyle = color
          ctx.globalAlpha = alpha
          ctx.fill()

          // Subtle glow
          const gradient = ctx.createRadialGradient(px, py, 0, px, py, particleSize * 1.5)
          gradient.addColorStop(0, `rgba(210, 180, 140, ${alpha * 0.3})`)
          gradient.addColorStop(1, "rgba(210, 180, 140, 0)")
          ctx.beginPath()
          ctx.arc(px, py, particleSize * 1.5, 0, Math.PI * 2)
          ctx.fillStyle = gradient
          ctx.globalAlpha = 1
          ctx.fill()
        }

        // Central dust cloud that expands and fades
        const cloudRadius = 25 + progress * 40
        const cloudGradient = ctx.createRadialGradient(
          effect.position.x, effect.position.y - progress * 15, 0,
          effect.position.x, effect.position.y - progress * 15, cloudRadius
        )
        cloudGradient.addColorStop(0, `rgba(139, 115, 85, ${opacity * 0.4})`)
        cloudGradient.addColorStop(0.5, `rgba(160, 82, 45, ${opacity * 0.2})`)
        cloudGradient.addColorStop(1, "rgba(139, 115, 85, 0)")
        ctx.beginPath()
        ctx.arc(effect.position.x, effect.position.y - progress * 15, cloudRadius, 0, Math.PI * 2)
        ctx.fillStyle = cloudGradient
        ctx.fill()
      } else if (isSporeCloud) {
        // Spore cloud effect - expanding green cloud with floating spore particles
        const cloudRadius = effect.size.width * 0.5 * (0.3 + progress * 0.7)

        // Draw main cloud
        const cloudGradient = ctx.createRadialGradient(
          effect.position.x, effect.position.y, 0,
          effect.position.x, effect.position.y, cloudRadius
        )
        cloudGradient.addColorStop(0, `rgba(144, 238, 144, ${opacity * 0.4})`) // Light green center
        cloudGradient.addColorStop(0.5, `rgba(50, 205, 50, ${opacity * 0.3})`) // Lime green
        cloudGradient.addColorStop(0.8, `rgba(34, 139, 34, ${opacity * 0.15})`) // Forest green
        cloudGradient.addColorStop(1, "rgba(0, 100, 0, 0)") // Dark green fade
        ctx.beginPath()
        ctx.arc(effect.position.x, effect.position.y, cloudRadius, 0, Math.PI * 2)
        ctx.fillStyle = cloudGradient
        ctx.fill()

        // Draw floating spore particles
        const particleCount = 12
        for (let i = 0; i < particleCount; i++) {
          const angle = (i / particleCount) * Math.PI * 2 + progress * Math.PI * 0.5
          const baseDistance = cloudRadius * 0.6
          const floatOffset = Math.sin(progress * Math.PI * 3 + i) * 10
          const distance = baseDistance + floatOffset
          const rise = progress * 30 * (0.5 + (i % 3) * 0.25)

          const px = effect.position.x + Math.cos(angle) * distance
          const py = effect.position.y - rise + Math.sin(angle) * distance * 0.2

          const particleSize = 3 + (i % 3)
          const particleOpacity = opacity * (0.6 + Math.sin(progress * Math.PI * 4 + i) * 0.3)

          // Spore particle with glow
          ctx.beginPath()
          ctx.arc(px, py, particleSize + 2, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(144, 238, 144, ${particleOpacity * 0.3})`
          ctx.fill()

          ctx.beginPath()
          ctx.arc(px, py, particleSize, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(50, 205, 50, ${particleOpacity})`
          ctx.fill()
        }
      } else if (isToxicBurst) {
        // Toxic burst effect - rapid expanding toxic explosion
        const burstRadius = effect.size.width * 0.5 * (0.2 + progress * 0.8)

        // Draw toxic cloud
        const burstGradient = ctx.createRadialGradient(
          effect.position.x, effect.position.y, 0,
          effect.position.x, effect.position.y, burstRadius
        )
        burstGradient.addColorStop(0, `rgba(154, 205, 50, ${opacity * 0.5})`) // Yellow-green center
        burstGradient.addColorStop(0.4, `rgba(107, 142, 35, ${opacity * 0.4})`) // Olive
        burstGradient.addColorStop(0.7, `rgba(85, 107, 47, ${opacity * 0.2})`) // Dark olive
        burstGradient.addColorStop(1, "rgba(85, 107, 47, 0)")
        ctx.beginPath()
        ctx.arc(effect.position.x, effect.position.y, burstRadius, 0, Math.PI * 2)
        ctx.fillStyle = burstGradient
        ctx.fill()

        // Toxic splash particles
        const splashCount = 8
        for (let i = 0; i < splashCount; i++) {
          const angle = (i / splashCount) * Math.PI * 2 + progress * Math.PI
          const splashDist = burstRadius * (0.5 + progress * 0.5)
          const px = effect.position.x + Math.cos(angle) * splashDist
          const py = effect.position.y + Math.sin(angle) * splashDist * 0.7 - progress * 15

          ctx.beginPath()
          ctx.arc(px, py, 4 * (1 - progress * 0.5), 0, Math.PI * 2)
          ctx.fillStyle = `rgba(173, 255, 47, ${opacity * 0.7})`
          ctx.fill()
        }
      } else {
        // Default particle effect (existing code)
        // Draw multiple particles rising and fading
        const particleCount = 8
        for (let i = 0; i < particleCount; i++) {
          const angle = (i / particleCount) * Math.PI * 2 + progress * Math.PI
          const distance = 15 + progress * 30 // Expand outward
          const rise = progress * 40 // Rise upward

          const px = effect.position.x + Math.cos(angle) * distance
          const py = effect.position.y - rise + Math.sin(angle) * distance * 0.3

          // Use colors from the effect
          const color = effect.colors[i % effect.colors.length]

          // Parse color and apply opacity
          ctx.beginPath()
          ctx.arc(px, py, 4 * (1 - progress * 0.5), 0, Math.PI * 2)
          ctx.fillStyle = color
          ctx.globalAlpha = opacity * 0.8
          ctx.fill()
        }

        // Draw a central glow
        const gradient = ctx.createRadialGradient(
          effect.position.x, effect.position.y - progress * 20, 0,
          effect.position.x, effect.position.y - progress * 20, effect.size.width * (0.5 + progress * 0.3)
        )
        const primaryColor = effect.colors[0] || "#fbbf24"
        gradient.addColorStop(0, primaryColor)
        gradient.addColorStop(0.5, primaryColor + "80") // 50% opacity
        gradient.addColorStop(1, primaryColor + "00") // 0% opacity
        ctx.beginPath()
        ctx.arc(effect.position.x, effect.position.y - progress * 20, effect.size.width * (0.5 + progress * 0.3), 0, Math.PI * 2)
        ctx.fillStyle = gradient
        ctx.globalAlpha = opacity * 0.6
        ctx.fill()
      }

      ctx.restore()
    }

    // Draw skill targeting preview
    // Use mouseWorldPosition (desktop) or mobileTargetPreviewPos (mobile)
    const targetPreviewPos = mouseWorldPosition || mobileTargetPreviewPos
    if (isTargetingSkill && hero && hero.position && pendingSkillInfo) {
      ctx.save()

      const skillType = pendingSkillInfo.type
      const rangeInPixels = (pendingSkillInfo.range || 8) * TILE_SIZE

      // Draw range circle around hero for skills with a range
      if (rangeInPixels > 0) {
        ctx.beginPath()
        ctx.arc(hero.position.x, hero.position.y, rangeInPixels, 0, Math.PI * 2)
        ctx.fillStyle = "rgba(255, 255, 255, 0.08)"
        ctx.fill()
        ctx.strokeStyle = "rgba(255, 255, 255, 0.3)"
        ctx.lineWidth = 2
        ctx.setLineDash([8, 8])
        ctx.stroke()
        ctx.setLineDash([])
      }

      if (targetPreviewPos && (skillType === "projectile" || skillType === "dash")) {
        // Draw line from hero through cursor (skillshot preview)
        const dx = targetPreviewPos.x - hero.position.x
        const dy = targetPreviewPos.y - hero.position.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist > 0) {
          // Normalize direction and extend to range
          const dirX = dx / dist
          const dirY = dy / dist
          const endX = hero.position.x + dirX * rangeInPixels
          const endY = hero.position.y + dirY * rangeInPixels

          // Draw the targeting line with accurate width
          // Dash skills (Vanguard Rush) use hitRadius=40, skill effects (Spectral Blade) use hitRadius=24
          const hitDiameter = skillType === "dash" ? 80 : 48
          ctx.beginPath()
          ctx.moveTo(hero.position.x, hero.position.y)
          ctx.lineTo(endX, endY)
          ctx.strokeStyle = "rgba(255, 255, 255, 0.4)"
          ctx.lineWidth = hitDiameter
          ctx.lineCap = "round"
          ctx.stroke()

          // Draw a smaller inner line for contrast
          ctx.beginPath()
          ctx.moveTo(hero.position.x, hero.position.y)
          ctx.lineTo(endX, endY)
          ctx.strokeStyle = "rgba(255, 255, 255, 0.8)"
          ctx.lineWidth = skillType === "dash" ? 4 : 3
          ctx.stroke()

          // Draw animated arrow traveling along the line (looping)
          const arrowTime = (gameNow() % 1000) / 1000 // 0-1 over 1 second loop
          const arrowProgress = arrowTime // Arrow travels from hero to end
          const arrowX = hero.position.x + dirX * rangeInPixels * arrowProgress
          const arrowY = hero.position.y + dirY * rangeInPixels * arrowProgress

          // Arrow fades out as it travels
          const arrowOpacity = 1 - arrowProgress * 0.8

          // Draw arrow triangle pointing in direction of travel
          const arrowSize = 12
          const angle = Math.atan2(dirY, dirX)

          ctx.save()
          ctx.translate(arrowX, arrowY)
          ctx.rotate(angle)
          ctx.beginPath()
          ctx.moveTo(arrowSize, 0) // Tip
          ctx.lineTo(-arrowSize * 0.6, -arrowSize * 0.5) // Back left
          ctx.lineTo(-arrowSize * 0.3, 0) // Inner notch
          ctx.lineTo(-arrowSize * 0.6, arrowSize * 0.5) // Back right
          ctx.closePath()
          ctx.fillStyle = `rgba(255, 255, 255, ${arrowOpacity})`
          ctx.fill()
          ctx.restore()

          // Draw range circle at the end point
          ctx.beginPath()
          ctx.arc(endX, endY, 8, 0, Math.PI * 2)
          ctx.fillStyle = "rgba(255, 255, 255, 0.8)"
          ctx.fill()
        }
      } else if (targetPreviewPos && skillType === "summon" && pendingSkillInfo.summonType === "siege-tower") {
        // Draw animated turret sprite preview at cursor
        drawTurretPreview(ctx, targetPreviewPos, 0.5)
      } else if (targetPreviewPos && skillType === "targeted-dot") {
        // Draw AoE circle preview for targeted-dot skills (Entangling Roots)
        const aoeRadius = (pendingSkillInfo.range || 1.5) * TILE_SIZE * 0.5

        ctx.beginPath()
        ctx.arc(targetPreviewPos.x, targetPreviewPos.y, aoeRadius, 0, Math.PI * 2)
        ctx.fillStyle = "rgba(34, 139, 34, 0.3)"
        ctx.fill()
        ctx.strokeStyle = "rgba(34, 139, 34, 0.7)"
        ctx.lineWidth = 2
        ctx.stroke()

        // Draw center targeting indicator
        ctx.beginPath()
        ctx.arc(targetPreviewPos.x, targetPreviewPos.y, 8, 0, Math.PI * 2)
        ctx.fillStyle = "rgba(34, 139, 34, 0.8)"
        ctx.fill()
      }

      ctx.restore()
    }

    // Draw item targeting preview (teleport, lure)
    if (isTargetingItem && hero && pendingItemInfo) {
      ctx.save()

      if (targetPreviewPos) {
        if (pendingItemInfo.effectType === "teleport") {
          // Draw teleport destination preview - swirling portal effect
          const portalRadius = 24

          // Outer glow
          const gradient = ctx.createRadialGradient(
            targetPreviewPos.x, targetPreviewPos.y, 0,
            targetPreviewPos.x, targetPreviewPos.y, portalRadius * 1.5
          )
          gradient.addColorStop(0, "rgba(0, 206, 209, 0.6)")
          gradient.addColorStop(0.5, "rgba(0, 206, 209, 0.3)")
          gradient.addColorStop(1, "rgba(0, 206, 209, 0)")
          ctx.beginPath()
          ctx.arc(targetPreviewPos.x, targetPreviewPos.y, portalRadius * 1.5, 0, Math.PI * 2)
          ctx.fillStyle = gradient
          ctx.fill()

          // Inner portal ring
          ctx.beginPath()
          ctx.arc(targetPreviewPos.x, targetPreviewPos.y, portalRadius, 0, Math.PI * 2)
          ctx.strokeStyle = "rgba(0, 206, 209, 0.9)"
          ctx.lineWidth = 3
          ctx.stroke()

          // Center indicator
          ctx.beginPath()
          ctx.arc(targetPreviewPos.x, targetPreviewPos.y, 6, 0, Math.PI * 2)
          ctx.fillStyle = "rgba(0, 206, 209, 0.95)"
          ctx.fill()

        } else if (pendingItemInfo.effectType === "lure") {
          // Draw lure AoE preview - golden circle
          const lureRadius = pendingItemInfo.radius || 400

          // Outer attraction zone
          ctx.beginPath()
          ctx.arc(targetPreviewPos.x, targetPreviewPos.y, lureRadius, 0, Math.PI * 2)
          ctx.fillStyle = "rgba(218, 165, 32, 0.15)"
          ctx.fill()
          ctx.strokeStyle = "rgba(218, 165, 32, 0.5)"
          ctx.lineWidth = 2
          ctx.setLineDash([10, 10])
          ctx.stroke()
          ctx.setLineDash([])

          // Inner indicator
          ctx.beginPath()
          ctx.arc(targetPreviewPos.x, targetPreviewPos.y, 16, 0, Math.PI * 2)
          ctx.fillStyle = "rgba(218, 165, 32, 0.6)"
          ctx.fill()
          ctx.strokeStyle = "rgba(218, 165, 32, 0.9)"
          ctx.lineWidth = 2
          ctx.stroke()

          // Center dot
          ctx.beginPath()
          ctx.arc(targetPreviewPos.x, targetPreviewPos.y, 5, 0, Math.PI * 2)
          ctx.fillStyle = "rgba(218, 165, 32, 0.95)"
          ctx.fill()
        }
      }

      ctx.restore()
    }

    ctx.restore()
  }, [
    enemies,
    towers,
    hero,
    projectiles,
    skillEffects,
    summonedUnits,
    level,
    selectedTowerId,
    selectedHeroId,
    selectedSummonId,
    ghostTower,
    isMenuOpen,
    viewport,
    renderWidth,
    renderHeight,
    landedPod,
    isDroppodSelected,
    isTargetingSkill,
    mouseWorldPosition,
    pendingSkillInfo,
    mobileTargetPreviewPos,
    isTargetingItem,
    pendingItemInfo,
    aoeVisualEffects,
    particleEffects,
    onParticleComplete,
    droppedItems,
    screenShakeOffset,
    laserBeamEffects,
    abductionAnimation,
    soldierSpawnPortraits,
    // Also read during drawing - without these, the canvas shows a stale frame
    // whenever the game loop isn't driving re-renders (paused, selections)
    corpses,
    barracksSoldiers,
    rootsEffects,
    fireEffects,
    selectedEnemyId,
    selectedSoldierId,
    selectedShrineMenuId,
    upgradeModifiers,
    upgradeState,
    activeItemEffects,
  ])

  /**
   * Convert screen coordinates to world coordinates
   */
  const screenToWorld = useCallback(
    (clientX: number, clientY: number): Position | null => {
      const canvas = canvasRef.current
      if (!canvas) return null

      const rect = canvas.getBoundingClientRect()
      // Use actual canvas dimensions for proper coordinate mapping
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height

      // Convert screen position to viewport position, then to world position
      const viewportX = (clientX - rect.left) * scaleX
      const viewportY = (clientY - rect.top) * scaleY

      return {
        x: viewportX / viewport.zoom + viewport.x,
        y: viewportY / viewport.zoom + viewport.y,
      }
    },
    [viewport],
  )

  /**
   * Get overlay position for tile highlight
   */
  const getTileOverlayPosition = useCallback(
    (worldPos: Position) => {
      const canvas = canvasRef.current
      if (!canvas) return null

      const rect = canvas.getBoundingClientRect()
      // Use actual canvas dimensions for proper coordinate mapping
      const scaleX = rect.width / canvas.width
      const scaleY = rect.height / canvas.height

      const tile = worldToTile(worldPos.x, worldPos.y)
      const screenX = (tile.tileX * TILE_SIZE - viewport.x) * viewport.zoom * scaleX
      const screenY = (tile.tileY * TILE_SIZE - viewport.y) * viewport.zoom * scaleY

      return {
        left: screenX,
        top: screenY,
        width: TILE_SIZE * scaleX * viewport.zoom,
        height: TILE_SIZE * scaleY * viewport.zoom,
      }
    },
    [viewport],
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const worldPos = screenToWorld(e.clientX, e.clientY)
      if (!worldPos) return

      // Track mouse position for skill targeting preview
      if (isTargetingSkill) {
        setMouseWorldPosition(worldPos)
      }

      // Check if hovering over any enemy
      let foundEnemy: Enemy | null = null
      for (const enemy of enemies) {
        const dx = worldPos.x - enemy.position.x
        const dy = worldPos.y - enemy.position.y
        if (dx * dx + dy * dy <= 24 * 24) {
          foundEnemy = enemy
          break
        }
      }

      if (foundEnemy) {
        setHoveredEnemy(foundEnemy)
        setTooltipPosition({ x: e.clientX, y: e.clientY })
      } else {
        setHoveredEnemy(null)
        setTooltipPosition(null)
      }
    },
    [enemies, screenToWorld, isTargetingSkill],
  )

  const handleMouseLeave = useCallback(() => {
    setHoveredEnemy(null)
    setTooltipPosition(null)
    setMouseWorldPosition(null)
  }, [])

  // Clear the stale targeting cursor when a targeting session ends, so the
  // next session doesn't briefly show the previous session's position
  useEffect(() => {
    if (!isTargetingSkill) {
      setMouseWorldPosition(null)
    }
  }, [isTargetingSkill])

  const ghostTowerOverlay = ghostTower?.valid ? getTileOverlayPosition(ghostTower.position) : null
  const selectedTileOverlay = selectedTilePosition ? getTileOverlayPosition(selectedTilePosition) : null

  // Calculate droppod screen position for clone count overlay
  const getDroppodScreenPosition = useCallback(() => {
    if (!landedPod || !landedPod.position || !canvasRef.current) return null
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    // Use canvas internal dimensions (which vary on desktop full-width mode)
    const scaleX = rect.width / canvas.width
    const scaleY = rect.height / canvas.height

    // Convert world position to screen position
    const screenX = (landedPod.position.x - viewport.x) * viewport.zoom * scaleX
    const screenY = (landedPod.position.y - viewport.y) * viewport.zoom * scaleY

    return { x: screenX, y: screenY }
  }, [landedPod, viewport])

  const droppodScreenPos = getDroppodScreenPosition()

  return (
    <TooltipProvider delayDuration={100}>
      <div ref={containerRef} id="game-canvas-container" className="flex items-center justify-center w-full h-full">
        <div className="relative">
          <canvas
            ref={canvasRef}
            id="game-canvas"
            width={VIEWPORT_WIDTH}
            height={VIEWPORT_HEIGHT}
            className=""
            style={{
              imageRendering: "pixelated",
              touchAction: "none",
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={onClick}
            onContextMenu={onRightClick}
          />

          {/* Selected tile overlay */}
          {selectedTilePosition && selectedTileOverlay && (
            <div
              id="selected-tile-overlay"
              className="absolute bg-game-success/30 animate-pulse pointer-events-none rounded-lg flex items-center justify-center z-40 select-none"
              style={{
                left: `${selectedTileOverlay.left}px`,
                top: `${selectedTileOverlay.top}px`,
                width: `${selectedTileOverlay.width}px`,
                height: `${selectedTileOverlay.height}px`,
              }}
            >
              <div
                className="text-foreground font-bold text-center select-none"
                style={{ fontSize: "10px", textShadow: "0 0 4px black, 0 0 8px black" }}
              >
                <div>Build</div>
                <div>Here</div>
              </div>
            </div>
          )}

          {/* Ghost tower overlay */}
          {ghostTower?.valid && ghostTowerOverlay && (
            <div
              id="tower-placement-overlay"
              className="absolute bg-game-success/30 animate-pulse pointer-events-none rounded-lg flex items-center justify-center z-50 select-none"
              style={{
                left: `${ghostTowerOverlay.left}px`,
                top: `${ghostTowerOverlay.top}px`,
                width: `${ghostTowerOverlay.width}px`,
                height: `${ghostTowerOverlay.height}px`,
              }}
            >
              <div
                className="text-foreground font-bold text-center select-none"
                style={{ fontSize: "10px", textShadow: "0 0 4px black, 0 0 8px black" }}
              >
                <div>Build</div>
                <div>Here</div>
              </div>
            </div>
          )}

          {/* Enemy tooltip */}
          {hoveredEnemy && tooltipPosition && (
            <div
              id="enemy-tooltip"
              className="fixed pointer-events-none z-50"
              style={{
                left: `${tooltipPosition.x}px`,
                top: `${tooltipPosition.y}px`,
                transform: "translate(10px, -50%)",
              }}
            >
              <div className="bg-game-surface border border-game-border text-foreground rounded-md px-3 py-1.5 text-sm shadow-md max-w-48">
                <div className="font-bold">{enemyTypes[hoveredEnemy.type]?.name || "Enemy"}</div>
                <div className="text-xs text-muted-foreground">{enemyTypes[hoveredEnemy.type]?.description || ""}</div>
                {/* Elite ability info */}
                {hoveredEnemy.eliteAbility && (hoveredEnemy.variant === "elite" || hoveredEnemy.variant === "unique") && (
                  <div className="mt-1 pt-1 border-t border-purple-500/30">
                    <div className="text-xs text-purple-300 font-semibold">
                      ★ {hoveredEnemy.eliteAbility.name}
                    </div>
                    <div className="text-xs text-purple-200/70">
                      {hoveredEnemy.eliteAbility.description}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Item drop messages - floating notifications when items drop */}
          {itemDropMessages.map((msg) => {
            // Calculate screen position from world position
            const canvas = canvasRef.current
            if (!canvas) return null
            const rect = canvas.getBoundingClientRect()
            const scaleX = rect.width / canvas.width
            const scaleY = rect.height / canvas.height
            const screenX = ((msg.position.x - viewport.x) * viewport.zoom) * scaleX
            const screenY = ((msg.position.y - viewport.y) * viewport.zoom) * scaleY

            // Calculate animation progress (3 second duration)
            const DURATION = 3000
            const elapsed = gameNow() - msg.startTime
            const progress = Math.min(elapsed / DURATION, 1)

            // Fade out in last 30%
            const opacity = progress > 0.7 ? 1 - ((progress - 0.7) / 0.3) : 1

            // Float upward
            const yOffset = -40 * progress

            if (progress >= 1) return null

            return (
              <div
                key={msg.id}
                className="absolute pointer-events-none z-50"
                style={{
                  left: `${screenX}px`,
                  top: `${screenY + yOffset}px`,
                  transform: "translate(-50%, -100%)",
                  opacity,
                }}
              >
                <div className="bg-game-surface border border-game-border rounded-md px-3 py-1.5 shadow-md">
                  <div
                    className="font-bold text-sm whitespace-nowrap"
                    style={{ color: msg.rarityColor }}
                  >
                    {msg.itemName}
                  </div>
                </div>
              </div>
            )
          })}

          {/* Clone count on droppod - bottom right aligned blue number with black shadow */}
          {landedPod && droppodScreenPos && cloneCount > 0 && (
            <div
              className="absolute pointer-events-none z-30"
              style={{
                left: `${droppodScreenPos.x + 20}px`,
                top: `${droppodScreenPos.y + 12}px`,
              }}
            >
              <span
                className="text-cyan-400 font-bold text-sm"
                style={{
                  textShadow: "2px 2px 0 black, -2px 2px 0 black, 2px -2px 0 black, -2px -2px 0 black, 0 2px 0 black, 0 -2px 0 black, 2px 0 0 black, -2px 0 0 black",
                }}
              >
                {cloneCount}
              </span>
            </div>
          )}

          {/* Droppod tutorial tooltip - Level 1 only */}
          {showDroppodTutorial && landedPod && droppodScreenPos && canvasRef.current && (() => {
            const canvas = canvasRef.current
            const rect = canvas.getBoundingClientRect()
            const scaleY = rect.height / canvas.height
            // Position 30px north of droppod in screen coordinates
            const offsetY = 30 * scaleY
            return (
              <div
                className="absolute pointer-events-none z-40"
                style={{
                  left: `${droppodScreenPos.x}px`,
                  top: `${droppodScreenPos.y - offsetY}px`,
                  transform: `translateX(-50%) translateY(-100%) scale(${viewport.zoom})`,
                  transformOrigin: "bottom center",
                }}
              >
                <div
                  className="relative bg-black border border-white/50 rounded-lg px-3 py-2 text-white text-center whitespace-nowrap"
                  style={{
                    fontSize: "12px",
                  }}
                >
                  <div>Clone hero & perma upgrade</div>
                  {/* Diamond pointer pointing DOWN */}
                  <div
                    className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-3 h-3 bg-black border-r border-b border-white/50 rotate-45"
                  />
                </div>
              </div>
            )
          })()}

        </div>
      </div>
    </TooltipProvider>
  )
}
